import styles from './LocationFilter.module.scss';
import { VdsCard } from '@volt/volt-extended-components-react';
import MapView, { MapViewProps } from '../map-view/MapView';
import { LatLng, LatLngBounds, latLngBounds, Map } from 'leaflet';
import { useCallback, useMemo, useState } from 'react';
import SearchBoundsLayer from '../search-bounds-layer/SearchBoundsLayer';
import { LocationFilterProps } from './useLocationFilterController';
import { ReactHeatmapLayer } from '../heatmap/react-heatmap-layer/ReactHeatmapLayer';
import LocationFilterHeader from './header/LocationFilterHeader';
import { latlngAustralia } from '../hooks/useLastMapPositionData';
import { constrain } from '@cas/utils/numberHelpers';
import AllGeofencesLayer from '../all-geofences-layer/AllGeofencesLayer';
import MapLoader from '../map-loader/MapLoader';

const MAX_LAT = 90;
const MIN_LAT = -90;
const MAX_LON = 180;
const MIN_LON = -180;

const fallbackMapProps = {
  initialCentre: latlngAustralia,
  initialZoom: 4,
};

type Coordinate = number | null | undefined;

const getBoundsFromProperties = (
  startLat: Coordinate,
  startLon: Coordinate,
  endLat: Coordinate,
  endLon: Coordinate,
): LatLngBounds | null => {
  const hasRequiredProperties =
    typeof startLat === 'number' &&
    typeof startLon === 'number' &&
    typeof endLat === 'number' &&
    typeof endLon === 'number';

  if (hasRequiredProperties) {
    return latLngBounds([new LatLng(startLat, startLon), new LatLng(endLat, endLon)]);
  }

  return null;
};

export interface MapBoundsZoom {
  southLat: number;
  northLat: number;
  eastLon: number;
  westLon: number;
  zoom: number;
}

export const LocationFilter = ({
  items,
  sLat,
  sLon,
  eLat,
  eLon,
  cellWidth,
  args,
  isFetching,
  updateSearchParams,
}: LocationFilterProps) => {
  const { startLat, startLon, endLat, endLon, queryKey = 'no-query-made' } = { ...args };

  const apiBounds = useMemo<LatLngBounds | null>(() => {
    return getBoundsFromProperties(sLat, sLon, eLat, eLon);
  }, [sLat, sLon, eLat, eLon]);

  const searchBounds = useMemo<LatLngBounds | null>(() => {
    return getBoundsFromProperties(startLat, startLon, endLat, endLon);
  }, [startLat, startLon, endLat, endLon]);

  const [mapBoundsZoom, setMapBoundsZoom] = useState<MapBoundsZoom | null>(null);

  const getMapBoundsZoom = useCallback((map: Map) => {
    const mapBounds = map.getBounds();

    const mapBoundsZoom: MapBoundsZoom = {
      northLat: constrain(mapBounds.getNorth(), MIN_LAT, MAX_LAT),
      westLon: constrain(mapBounds.getWest(), MIN_LON, MAX_LON),
      southLat: constrain(mapBounds.getSouth(), MIN_LAT, MAX_LAT),
      eastLon: constrain(mapBounds.getEast(), MIN_LON, MAX_LON),
      zoom: map.getZoom(),
    };

    setMapBoundsZoom(mapBoundsZoom);
  }, []);

  const onSearch = useCallback(() => {
    if (mapBoundsZoom) {
      updateSearchParams(mapBoundsZoom);
    }
  }, [updateSearchParams, mapBoundsZoom]);

  const onClearSearch = useCallback(() => {
    updateSearchParams(null);
  }, [updateSearchParams]);

  const showMap = typeof items !== 'undefined' && typeof cellWidth !== 'undefined';
  const hasHeatmapItems = !!items?.length;
  const showSearchBounds = !!searchBounds; // Only show if the user has selected a search area.
  const showLayersControl = Boolean(showSearchBounds || hasHeatmapItems);
  const showClear = Boolean(showMap && searchBounds);

  const mapProps = useMemo<MapViewProps>(() => {
    const commonProps: MapViewProps = {
      showLayersControl,
      animate: true,
      renderKey: queryKey,
      afterInitCallback: getMapBoundsZoom,
    };

    if (apiBounds) {
      // First, try to fit the map to the api response bounds if they exist.
      return { ...commonProps, bounds: apiBounds, padding: false };
    } else if (searchBounds) {
      // Then, attempt to fit the map to the saved search area if it exists.
      return { ...commonProps, bounds: searchBounds, padding: false };
    } else {
      // Finally, provide a stable fallback for intitial display.
      return { ...commonProps, ...fallbackMapProps };
    }
  }, [showLayersControl, apiBounds, searchBounds, queryKey, getMapBoundsZoom]);

  return (
    <VdsCard padding="spacing-00">
      <div className={styles.container}>
        <LocationFilterHeader
          onSearch={onSearch}
          onClearSearch={onClearSearch}
          showClear={showClear}
          disableSearch={!showMap || isFetching}
        />
        <MapLoader showMap={showMap} isFetching={isFetching} height={400}>
          <MapView {...mapProps}>
            {showMap && (
              <ReactHeatmapLayer
                items={items}
                cellWidth={cellWidth}
                onMapMoveEnd={getMapBoundsZoom}
              />
            )}
            {showSearchBounds && <SearchBoundsLayer bounds={apiBounds || searchBounds} />}
            <AllGeofencesLayer />
          </MapView>
        </MapLoader>
      </div>
    </VdsCard>
  );
};

export default LocationFilter;
